import React, { useState, useEffect, ReactElement } from 'react';
import { spacing } from '../../../style';
import styled from '@emotion/styled';
import { enabledOfferHighlights } from '../../../config/Master';
import { setLocalItem, getLocalItem } from '../../../common';
import SkeletonLoader from '../../../atoms/SkeletonLoader';

import OfferHighlightModal from '../../../modules/OfferHighlight/OfferHighlightModal';
import CompleteProfileBanner from '../../blocks/CompleteProfileBanner';
import MemberGrid from './MemberGrid';
import CustomGrid from './CustomGrid';
import useUnmount from '../../../hooks/useUnmount';
import useMixPanel from '../../../hooks/useMixPanel';
import breakpoints from '../../../theme/material/breakpoints';

const DashboardContainer = styled('div')({
  padding: `0 ${spacing.PADDING_SM.padding}`,
  maxWidth: 1400,
  margin: '0 auto',

  [breakpoints.up(769)]: {
    marginTop: 22,
    marginBottom: 22,
  },
});

interface DashboardProps {
  recentSearchResults: any;
  recent: any;
  newest: any;
  isFetching: any;
  profile: any;
  offers: any;
  favorites: any;
  featuredProfiles: any;
  isFetchingFavs: any;
}

const Dashboard = ({
  recentSearchResults,
  recent,
  newest,
  profile,
  offers,
  favorites,
  featuredProfiles,
  isFetchingFavs,
  ...rest
}: DashboardProps): ReactElement => {
  const isGenerous = profile && profile.profile && profile.profile.data.account_type.toLowerCase() === 'generous';
  const hasPendingOffers = profile.offer_counts && offers.highlights && offers.highlights.length !== 0;
  const [isModalOpen, setIsOpen] = useState(false);
  const { trackPageView } = useMixPanel();

  useUnmount(() => {
    trackPageView('dashboard-normal');
  });

  const onClose = () => {
    setIsOpen(false);
    offFirstDateNotification();
  };

  const offFirstDateNotification = () => {
    setLocalItem('wyp_offFirstDateOffers', true);
  };

  const renderOfferModal = () => {
    const offFirstDateOffers = getLocalItem('wyp_offFirstDateOffers');

    (offFirstDateOffers === null || offFirstDateOffers === 'false') && setIsOpen(true);
  };

  useEffect(() => {
    renderOfferModal();
  }, []);

  const renderFavSection = () => {
    if (favorites && favorites.length < 2) return;
    return <CustomGrid id="FavMembers" title="Your Favorites" members={favorites} {...rest} />;
  };

  const renderFeaturedProfiles = () => {
    if (featuredProfiles && featuredProfiles.length < 1) return;

    const gridID = isGenerous ? 'FeatMembers' : 'GenerousMembers';
    const gridTitle = isGenerous ? 'Featured' : 'Very Generous';

    return (
      <CustomGrid id={gridID} size={4} title={gridTitle} members={featuredProfiles} isFeatured firstSection {...rest} />
    );
  };

  const renderOfferHighlights = () => {
    if (hasPendingOffers && enabledOfferHighlights) {
      return (
        <OfferHighlightModal
          containerId="OfferHighlight"
          profile={profile}
          offer={offers.highlights[0]}
          onClose={onClose}
          isOpen={isModalOpen}
          setIsOpen={setIsOpen}
          {...rest}
          isFetching={offers.isFetching}
        />
      );
    }
  };

  const renderCompleteProfileBanner = () => {
    if (profile && !profile.mandatory_profile_completed) {
      return <CompleteProfileBanner type="basic" />;
    }
  };

  const renderNewestNearby = () => {
    return (
      newest &&
      newest.length > 0 && (
        <MemberGrid
          id="NewMembers"
          title="New members"
          criteria={{ distance: 50, order: 'newest' }}
          members={newest}
          firstSection={featuredProfiles && featuredProfiles.length < 1}
          {...rest}
        />
      )
    );
  };

  const renderRecentOnline = () => {
    return (
      recent &&
      recent.length > 0 && (
        <MemberGrid
          id="RecentlyOnline"
          title="Recently online"
          criteria={{ distance: 50, order: 'recent' }}
          members={recent}
          {...rest}
        />
      )
    );
  };

  return (
    <React.Fragment>
      <DashboardContainer>
        {renderCompleteProfileBanner()}
        {renderOfferHighlights()}
        {isFetchingFavs ? <SkeletonLoader width="100%" /> : renderFeaturedProfiles()}

        {/* Newest members nearby */}
        {renderNewestNearby()}

        {/* Members recently online */}
        {renderRecentOnline()}

        {/* Favorites Section */}
        {isFetchingFavs ? <SkeletonLoader width="100%" /> : renderFavSection()}

        {/* Recent Searches */}
        {recentSearchResults && Object.keys(recentSearchResults).length > 0 && (
          <MemberGrid id="LastSearch" title="Last Search" members={recentSearchResults} saveSearch {...rest} />
        )}
      </DashboardContainer>
    </React.Fragment>
  );
};

export default Dashboard;
